import { useState, useEffect, useRef } from "react";
import serviceimg1 from "../assets/services/1.jpg";
import serviceimg2 from "../assets/services/2.jpg";
import serviceimg3 from "../assets/services/3.png";
import { useTranslation } from "react-i18next";

const Services = () => {
  const { t, i18n } = useTranslation();
  
  const services = [
    {
      img: serviceimg1,
      title: t("service1title"),
      desc: t("service1desc"),
    },
    {
      img: serviceimg2,
      title: t("service2title"),
      desc: t("service2desc"),
    },
    {
      img: serviceimg3,
      title: t("service3title"),
      desc: t("service3desc"),
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setTimeout(() => setAnimated(true), 800);
          
          // Скрываем тултип через 5 секунд после появления секции
          setTimeout(() => {
            setShowTooltip(false);
          }, 5000);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Функция для смены активного индекса с обработкой клика
  const handleServiceClick = (index) => {
    setActiveIndex(index);
    // Если пользователь кликнул, скрываем тултип
    if (showTooltip) setShowTooltip(false);
  };

  return (
    <section
      ref={sectionRef}
      className="max-w-7xl m-auto py-[80px] px-5 flex flex-col items-center relative"
    >
      {/* TITLE */}
      <h1
        className={`font-['Cormorant_Garamond'] uppercase text-[#0058a3] text-[22px] sm:text-[32px] lg:text-[44px] tracking-[0.15em] text-center transition-all duration-700
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
      `}
      >
        {t("Thesun")}
      </h1>

      <p
        className={`text-center mb-10 max-w-2xl text-gray-600 transition-all duration-700
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
      `}
      >
        {t("convert")}
      </p>

      {/* Подсказка для пользователя */}
      <div
        className={`
          fixed bottom-6 left-1/2 max-sm:w-[90%] transform -translate-x-1/2 z-50
          transition-all duration-500
          ${showTooltip && isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
        `}
      >
        <div className="bg-black/90 backdrop-blur-md text-white max-sm:w-full px-6 py-3 rounded-full shadow-lg flex items-center gap-3">
          <svg className="w-5 h-5 text-yellow-500 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
          </svg>
          <span className="text-sm max-sm:text-xs font-medium">👆 {t("click_on_service") || "Click on any card to see details"}</span>
          <button 
            onClick={() => setShowTooltip(false)}
            className="ml-2 text-gray-400 hover:text-white transition"
          >
            ✕
          </button>
        </div>
      </div>

      {/* GRID */}
      <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-10 items-start">

        {/* LEFT */}
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative">
          {/* Анимированная стрелка для первого элемента (десктоп) */}
          {showTooltip && isVisible && (
            <div className="hidden sm:block absolute -top-8 left-1/4 animate-pulse">
              <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          )}
          
          {services.map((service, index) => {
            const active = activeIndex === index;

            return (
              <li
                key={index}
                onClick={() => handleServiceClick(index)}
                className={`
                  group cursor-pointer rounded-2xl overflow-hidden relative
                  bg-black/5
                  transition-all duration-500
                  ${index === 2 ? "sm:col-span-2" : ""}
                  ${active ? "ring-2 ring-yellow-500 shadow-lg" : "hover:shadow-md"}
                  ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
                `}
                style={{
                  transitionDelay: !animated ? `${index * 120}ms` : "0ms",
                }}
              >
                {/* Индикатор "кликни меня" для неактивных карточек */}
                {!active && showTooltip && index === 0 && (
                  <div className="absolute top-2 right-2 z-10 bg-yellow-500 text-black text-xs px-2 py-1 rounded-full animate-pulse">
                    👆 Click me
                  </div>
                )}

                {/* IMAGE */}
                <div className="h-[200px] sm:h-[260px] overflow-hidden">
                  <img
                    src={service.img}
                    alt={service.title}
                    className={`
                      w-full h-full object-cover
                      transition-transform duration-500
                      ${active ? "scale-105" : "group-hover:scale-110"}
                    `}
                    loading="lazy"
                  />
                </div>

                {/* TITLE */}
                <div className={`
                  p-3 backdrop-blur-lg w-full top-0 absolute text-center uppercase tracking-[0.2em] text-sm
                  transition-all duration-300
                  ${active 
                    ? 'bg-yellow-500 text-black' 
                    : 'bg-black/40 text-yellow-400 group-hover:bg-black/60'
                  }
                `}>
                  {service.title}
                </div>

                {/* Индикатор клика (иконка) */}
                <div className="absolute bottom-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg className="w-6 h-6 text-yellow-500 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>

                {/* MOBILE EXPAND TEXT */}
                <div
                  className={`
                    sm:hidden px-4 pb-3 pt-2 text-sm text-white/90 overflow-hidden bg-[#0058a3] transition-all duration-500
                    ${active ? "max-h-[120px] opacity-100" : "max-h-0 opacity-0"}
                  `}
                >
                  {service.desc}
                </div>
              </li>
            );
          })}
        </ul>

        {/* RIGHT (desktop only) */}
        <div className="hidden xl:flex flex-col max-w-xl">
          <h2 className="text-[#0058a3] font-['Cormorant_Garamond'] text-[32px] font-light tracking-[0.15em] mb-6 uppercase">
            {t("Whychoose")}
          </h2>

          <p className="text-gray-700 mb-6">
            {t("Weoffer")}
          </p>

          <div className="bg-gradient-to-r from-yellow-500/10 to-transparent p-6 rounded-xl transition-all duration-300">
            <p className="text-lg font-medium text-black leading-relaxed">
              {services[activeIndex].desc}
            </p>
          </div>
          
          {/* Дополнительная подсказка для десктопа */}
          {showTooltip && (
            <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
              <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{t("click_on_card") || "Click on any card above to see details"}</span>
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

export default Services;